import { defineComponent } from 'vue';
import { QBtn, QIcon, QInput, QSeparator } from 'quasar';
import webSocket from '@/Services/webSocketService';

function lineDiff(before, after) {
    const a = (before || '').replace(/\r\n/g, '\n').split('\n');
    const b = (after || '').replace(/\r\n/g, '\n').split('\n');
    const rows = [];
    const max = Math.max(a.length, b.length);
    for (let i = 0; i < max; i++) {
        if (a[i] === b[i])
            continue;
        if (a[i] != null)
            rows.push('- ' + a[i]);
        if (b[i] != null)
            rows.push('+ ' + b[i]);
        if (rows.length > 200)
            break;
    }
    return rows.length ? rows.join('\n') : 'No text changes.';
}

export default defineComponent({
    name: 'flow-v2-ask-ai',
    components: { QBtn, QIcon, QInput, QSeparator },
    props: {
        open: { type: Boolean, default: false },
        flowId: { type: String, default: '' },
        itemType: { type: String, default: '' },
        methodName: { type: String, default: '' },
        readOnly: { type: Boolean, default: false },
        selectedIds: { type: Array, default: () => [] },
        currentBody: { type: String, default: '' },
    },
    emits: ['close', 'applied'],
    data() {
        return {
            draft: '',
            busy: false,
            status: '',
            messages: [],
            proposal: null,
            showJson: false,
            selectedPreview: null,
        };
    },
    computed: {
        chip() {
            return this.itemType === 'APIHeader' ? 'API' : 'Flow';
        },
        selectionLabel() {
            const n = (this.selectedIds || []).length;
            if (!n)
                return '';
            return n === 1 ? '1 step selected' : `${n} steps selected`;
        },
        placeholder() {
            if (this.selectionLabel)
                return 'Change the selected steps…';
            return this.itemType === 'APIHeader'
                ? 'Describe the change to this method…'
                : 'Describe the journey you want…';
        },
        starters() {
            if (this.messages.length)
                return [];
            if (this.itemType === 'APIHeader')
                return ['Read the caller id and return the customer record.'];
            return ['Answer the phone, play a greeting, and route on 1 and 2.'];
        },
        previewItems() {
            return this.proposal?.preview || [];
        },
        showPreview() {
            return this.itemType !== 'APIHeader' && this.previewItems.length > 0;
        },
        previewBounds() {
            let minX = 0;
            let minY = 0;
            let maxX = 200;
            let maxY = 80;
            this.previewItems.forEach(item => {
                const x = Number(item.x) || 0;
                const y = Number(item.y) || 0;
                minX = Math.min(minX, x);
                minY = Math.min(minY, y);
                maxX = Math.max(maxX, x + 160);
                maxY = Math.max(maxY, y + 70);
            });
            return { minX, minY, maxX, maxY };
        },
        previewScale() {
            const bounds = this.previewBounds;
            return Math.min(1, 360 / Math.max(240, bounds.maxX - bounds.minX));
        },
        previewHeight() {
            const bounds = this.previewBounds;
            return Math.ceil((bounds.maxY - bounds.minY) * this.previewScale) + 8;
        },
        previewLines() {
            const byId = {};
            this.previewItems.forEach(item => { byId[item.id] = item; });
            const scale = this.previewScale;
            const bounds = this.previewBounds;
            const lines = [];
            this.previewItems.forEach(item => {
                (item.links || []).forEach(link => {
                    const target = byId[link.targetId];
                    if (!target)
                        return;
                    lines.push({
                        x1: ((Number(item.x) || 0) - bounds.minX + 73) * scale,
                        y1: ((Number(item.y) || 0) - bounds.minY + 28) * scale,
                        x2: ((Number(target.x) || 0) - bounds.minX + 73) * scale,
                        y2: ((Number(target.y) || 0) - bounds.minY + 28) * scale,
                    });
                });
            });
            return lines;
        },
    },
    watch: {
        flowId() {
            this.messages = [];
            this.proposal = null;
            this.selectedPreview = null;
            this.status = '';
        },
    },
    methods: {
        onStatus(text) {
            this.status = text || '';
        },
        onValidation(content) {
            const errors = Array.isArray(content) ? content.filter(e => e && (e.Message || e.message)) : [];
            if (!errors.length)
                return;
            this.messages.push({
                role: 'assistant',
                text: 'The editor still reports errors.',
                errors: errors.map(e => e.Message || e.message),
                offerFix: true,
            });
        },
        askFix() {
            const last = [...this.messages].reverse().find(m => m.offerFix);
            const text = last?.errors?.length
                ? 'Fix these errors: ' + last.errors.join('; ')
                : 'Fix the remaining errors.';
            this.draft = text;
            this.send();
        },
        async send(text) {
            const message = (text || this.draft || '').trim();
            if (!message || this.busy || this.readOnly || !this.flowId)
                return;
            this.draft = '';
            this.proposal = null;
            this.showJson = false;
            this.selectedPreview = null;
            this.messages.push({ role: 'user', text: message });
            this.busy = true;
            this.status = 'Working';
            try {
                const raw = await webSocket.AskAiTurn(this.flowId, message, JSON.stringify(this.selectedIds || []));
                const result = typeof raw === 'string' ? JSON.parse(raw) : raw;
                if (!result?.ok) {
                    this.messages.push({ role: 'assistant', text: result?.error || 'Ask AI is unavailable.' });
                    return;
                }
                const ops = Array.isArray(result.ops) ? result.ops : [];
                if (result.proposalId && ops.length) {
                    this.proposal = {
                        id: result.proposalId,
                        summary: result.summary || result.answer || '',
                        ops,
                        errors: result.errors || [],
                        canApply: !!result.canApply,
                        body: ops.find(op => op.op === 'setBody')?.body || '',
                        preview: Array.isArray(result.preview?.items) ? result.preview.items : [],
                    };
                    this.selectedPreview = null;
                } else {
                    this.messages.push({ role: 'assistant', text: result.answer || result.summary || '' });
                }
            }
            catch (err) {
                this.messages.push({ role: 'assistant', text: 'Ask AI is unavailable.' });
            }
            finally {
                this.busy = false;
                this.status = '';
            }
        },
        discard() {
            this.proposal = null;
            this.showJson = false;
            this.selectedPreview = null;
        },
        async apply() {
            if (!this.proposal?.canApply || this.busy)
                return;
            this.busy = true;
            this.$emit('applied');
            try {
                const raw = await webSocket.AskAiApply(this.flowId, this.proposal.id);
                const result = typeof raw === 'string' ? JSON.parse(raw) : raw;
                if (!result?.ok) {
                    this.messages.push({ role: 'assistant', text: result?.error || 'Ask AI could not apply that change.' });
                    return;
                }
                this.messages.push({ role: 'assistant', text: 'Applied. Undo restores this change.' });
                this.proposal = null;
            }
            catch (err) {
                this.messages.push({ role: 'assistant', text: 'Ask AI could not apply that change.' });
            }
            finally {
                this.busy = false;
            }
        },
        iconStyle(item) {
            const scale = this.previewScale;
            const bounds = this.previewBounds;
            const selected = this.selectedPreview && this.selectedPreview.id === item.id;
            return {
                position: 'absolute',
                left: (((Number(item.x) || 0) - bounds.minX) * scale) + 'px',
                top: (((Number(item.y) || 0) - bounds.minY) * scale) + 'px',
                width: (146 * scale) + 'px',
                minHeight: (36 * scale) + 'px',
                padding: '4px 6px',
                borderRadius: '6px',
                border: selected ? '2px solid #3949ab' : '1px solid #9fa8da',
                background: '#e8eaf6',
                fontSize: '11px',
                cursor: 'pointer',
                overflow: 'hidden',
            };
        },
        argumentRows(item) {
            const args = item?.arguments || {};
            return Object.keys(args).map(key => ({ key, value: this.formatArgument(args[key]) }));
        },
        formatArgument(value) {
            if (value == null)
                return '';
            if (typeof value === 'object')
                return JSON.stringify(value);
            return String(value);
        },
        linkCaption(link) {
            const target = this.previewItems.find(item => item.id === link.targetId);
            const name = link.name || 'Link';
            return target ? `${name} → ${target.caption || target.toolName || 'step'}` : `${name} → ${link.targetId || ''}`;
        },
        opLabel(op) {
            const item = op.item || {};
            const caption = item.Caption || item.caption || op.instanceId || op.InstanceID || '';
            switch (op.op) {
                case 'add':
                    return `Add ${item.Caption || item.Type || 'step'}`;
                case 'remove':
                    return `Remove ${caption || 'step'}`;
                case 'updateArgs':
                    return `Change arguments ${caption}`;
                case 'updateOutput':
                    return `Change outputs ${caption}`;
                case 'setMatchPorts':
                    return `Change branches ${caption}`;
                case 'setLinks': {
                    const names = (op.links || op.Links || []).map(link => link && (link.Label || link.Name)).filter(Boolean).join(', ');
                    const who = item.Caption || item.caption || '';
                    return `Change links${who ? ` ${who}` : ''}${names ? `: ${names}` : ''}`;
                }
                case 'removeLink':
                    return `Remove a link ${caption}`;
                case 'addLocal':
                    return `Add variable ${op.local?.Name || ''}`;
                case 'updateLocal':
                    return `Change variable ${op.local?.Name || ''}`;
                case 'removeLocal':
                    return `Remove variable ${op.name || op.Name || ''}`;
                case 'setBody':
                    return 'Replace the method source';
                default:
                    return op.op || 'Change';
            }
        },
    },
    render() {
        if (!this.open)
            return null;
        const bodyDiff = this.proposal?.body
            ? lineDiff(this.currentBody, this.proposal.body)
            : '';
        return (
            <div
                class="column no-wrap bg-white"
                style={{
                    position: 'absolute',
                    top: '0',
                    right: '0',
                    bottom: '0',
                    width: '420px',
                    maxWidth: '100%',
                    zIndex: 30,
                    borderLeft: '1px solid #e0e0e0',
                    boxShadow: '-8px 0 24px rgba(0,0,0,0.08)',
                }}
            >
                <div class="row items-center q-px-md q-py-sm">
                    <div class="col">
                        <div class="text-subtitle2">{this.methodName || 'Ask AI'}</div>
                        <div class="text-caption text-grey-7">
                            {this.chip}
                            {this.selectionLabel ? ` · ${this.selectionLabel}` : ''}
                        </div>
                    </div>
                    <QBtn flat dense icon="mdi-close" onClick={() => this.$emit('close')} />
                </div>
                <QSeparator />
                <div class="col q-pa-md" style={{ overflow: 'auto' }}>
                    {this.messages.map((message, index) => (
                        <div key={index} class="q-mb-sm" style={{ whiteSpace: 'pre-wrap' }}>
                            <div class="text-caption text-grey-7">{message.role === 'user' ? 'You' : 'Ask AI'}</div>
                            <div>{message.text}</div>
                            {message.offerFix ? (
                                <QBtn class="q-mt-xs" dense no-caps outline color="primary" label="Fix these errors" disable={this.busy || this.readOnly} onClick={() => this.askFix()} />
                            ) : null}
                        </div>
                    ))}
                    {this.starters.map(prompt => (
                        <QBtn key={prompt} class="q-mb-sm" no-caps outline color="primary" label={prompt} disable={this.busy || this.readOnly} onClick={() => this.send(prompt)} />
                    ))}
                    {this.proposal ? (
                        <div class="q-pa-sm" style={{ border: '1px solid #d0d0d0', borderRadius: '8px' }}>
                            <div class="text-subtitle2 q-mb-xs">Proposed change</div>
                            <div class="q-mb-sm" style={{ whiteSpace: 'pre-wrap' }}>{this.proposal.summary}</div>
                            {this.showPreview ? (
                                <div class="q-mb-sm">
                                    <div style={{ position: 'relative', height: this.previewHeight + 'px', background: '#fafafa', borderRadius: '6px', overflow: 'auto' }}>
                                        <svg style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', pointerEvents: 'none' }}>
                                            {this.previewLines.map((line, index) => (
                                                <line key={index} x1={line.x1} y1={line.y1} x2={line.x2} y2={line.y2} stroke="#5c6bc0" strokeWidth="1.5" />
                                            ))}
                                        </svg>
                                        {this.previewItems.map(item => (
                                            <div key={item.id} style={this.iconStyle(item)} onClick={() => { this.selectedPreview = item; }}>
                                                <div style={{ fontWeight: 600 }}>{item.caption || 'Step'}</div>
                                                <div class="text-grey-8">{item.toolName}</div>
                                            </div>
                                        ))}
                                    </div>
                                    {this.selectedPreview ? (
                                        <div class="q-mt-xs text-caption">
                                            <div><b>{this.selectedPreview.caption || 'Step'}</b> · {this.selectedPreview.toolName}</div>
                                            {this.argumentRows(this.selectedPreview).map(row => (
                                                <div key={row.key} style={{ wordBreak: 'break-word' }}>{row.key}: {row.value}</div>
                                            ))}
                                            {(this.selectedPreview.links || []).map((link, index) => (
                                                <div key={index}>{this.linkCaption(link)}</div>
                                            ))}
                                        </div>
                                    ) : null}
                                </div>
                            ) : null}
                            {this.proposal.ops.filter(op => op.op !== 'setBody').map((op, index) => (
                                <div key={index} class="text-body2">{this.opLabel(op)}</div>
                            ))}
                            {bodyDiff ? (
                                <pre class="q-mt-sm" style={{ whiteSpace: 'pre-wrap', fontSize: '12px' }}>{bodyDiff}</pre>
                            ) : null}
                            {(this.proposal.errors || []).map((error, index) => (
                                <div key={'e' + index} class="text-negative q-mt-xs">{error}</div>
                            ))}
                            <QBtn class="q-mt-sm" flat dense no-caps label={this.showJson ? 'Hide details' : 'View details'} onClick={() => { this.showJson = !this.showJson; }} />
                            {this.showJson ? (
                                <pre style={{ whiteSpace: 'pre-wrap', fontSize: '11px' }}>{JSON.stringify(this.proposal.ops, null, 2)}</pre>
                            ) : null}
                            <div class="row q-gutter-sm q-mt-sm">
                                <QBtn unelevated no-caps color="primary" label="Apply" disable={!this.proposal.canApply || this.busy || this.readOnly} onClick={() => this.apply()} />
                                <QBtn flat no-caps label="Discard" disable={this.busy} onClick={() => this.discard()} />
                            </div>
                        </div>
                    ) : null}
                    {this.status ? <div class="text-caption text-grey-7 q-mt-sm">{this.status}</div> : null}
                </div>
                <QSeparator />
                <div class="q-pa-sm">
                    <QInput
                        outlined
                        dense
                        autogrow
                        type="textarea"
                        modelValue={this.draft}
                        onUpdate:modelValue={(v) => { this.draft = v; }}
                        placeholder={this.placeholder}
                        disable={this.busy || this.readOnly}
                        onKeydown={(e) => {
                            if (e.key === 'Enter' && !e.shiftKey) {
                                e.preventDefault();
                                this.send();
                            }
                        }}
                    />
                    <div class="row justify-end q-mt-xs">
                        <QBtn unelevated no-caps color="primary" label="Ask" disable={this.busy || this.readOnly || !this.draft.trim()} onClick={() => this.send()} />
                    </div>
                </div>
            </div>
        );
    },
});
